import { trigger, state, style, transition, animate } from '@angular/animations';
import { formatNumber } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, HostListener, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { DialogConfirmComponent } from 'src/app/dialog/dialog-confirm/dialog-confirm.component';
import { ComponentCanDeactivate } from 'src/app/_guard/pending-changes.guard';
import { AppService, GRSSResponse, ValueIdPair } from 'src/app/_services/app.service';
import { ContributeEditComponent } from './contribute-edit/contribute-edit.component';
import { AddTradeComponent } from './add-trade/add-trade.component';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

@Component({
    selector: 'app-contribute-transactional',
    templateUrl: './contribute-transactional.component.html',
    styleUrls: ['./contribute-transactional.component.css'],
    animations: [
        trigger('slider', [
            state('*', style({
                height: 0
            })),
            state('show', style({
                height: '*'
            })),
            transition('* <=> show', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')])
        ])
    ],
    standalone: false
})

export class ContributeTransactionalComponent implements OnInit, OnDestroy, ComponentCanDeactivate, AfterViewInit {

  @ViewChild('spreadsheetInput') spreadsheetInput: ElementRef;
  @ViewChild('transactionInput') transactionInput: ElementRef;
  @ViewChild('sortTrans', { static: false }) sortTrans: MatSort;
  @ViewChild('pgTrans', { read: MatPaginator }) pgTrans: MatPaginator;

  contributeMarket: ContributeMarket;
  contributeMarketTemp: ContributeMarket;
  hasCurrentContributions: boolean = false;
  instrumentLength: number = 0;
  timeToOpenOrClose: string;
  displayColumnsAll: string[] = [
    'name',
    'space',
    'level',
    'newRate',
    'newSpread',
    'assessedVolume',
    'fsaCOF',
    'actions'
  ];
  displayColumns: string[] = [
    'name',
    'newRate',
    'comments'
  ];
  dataSourceNew;
  canEnablebtnSubmit: boolean = true;
  hasSubmitted: boolean = false;
  currentTimeColumn: string = '';
  intervals: any[] = [];
  isLoading: boolean = false;
  shortdescription: string = '';
  local: string = 'en-US';
  discardChanges: boolean = false;
  reasons: ValueIdPair[] = [];
  uploadedSpreadsheetFile: any;
  spreadsheetFilename: string = '';
  hasUploadedAcceptedSpreadsheetFile: Boolean = false;
  spreadsheetUpload: ContributionsFileUpload;
  uploadedXmlFile: any;
  xmlFilename: string = '';
  hasUploadedAcceptedXmlFile: Boolean = false;
  xmlUpload: ContributionsFileUpload;
  allowTransactions: boolean = false;
  allowSubmissions: boolean = false;
  dataSourceTrans;
  translist: Transaction[] = [];
  displayColumnsTrans: string[] = [
    'state',
    'contributed',
    'currency',
    'status',
    'cpSector',
    'type',
    'instrumentType',
    'identifier',
    'tradeDate',
    'settlementDate',
    'maturityDate',
    'nominalAmount',
    'rate',
    'comments',
    'actions'
  ];
  transRecordsLength: number = 0;
  searchKey: string;

  // tslint:disable-next-line: max-line-length
  constructor(private router: Router, @Inject(LOCALE_ID) public localeID: string, private translate: TranslateService, private dialog: MatDialog, private route: ActivatedRoute, private http: HttpClient, @Inject('BASE_URL') private baseUrl: string, public service: AppService) {
    this.service.SetTranslation('my-general-markets/contribute');
    // import('@angular/common/locales/' + navigator.language + '.js').then(module => registerLocaleData(module.default));
  }

  public ngOnInit(): void {
    this.InitializeMarket();
  }

  public ngOnDestroy(): void {
    if (this.intervals.length > 0) {
      this.intervals.forEach(interval => {
        clearInterval(interval);
      });
    }
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    // insert logic to check if there are pending changes here;
    // returning true will navigate without confirmation
    // returning false will show a confirm dialog before navigating away

    let hasChanges: boolean = false;
    if (JSON.stringify(this.contributeMarket) !== JSON.stringify(this.contributeMarketTemp) && this.discardChanges === false) {
      hasChanges = true;
    }
    return !hasChanges;
  }

  public ngAfterViewInit(): void {
    this.route.queryParams.subscribe(params => {
      this.contributeMarket.marketId = params['ID'];
      this.CheckMarketType(this.contributeMarket.marketId).then(canProceed => {
        if (canProceed === true) {
          this.GetContributions();
          this.GetTransactions();
          this.SetShortDescription();
          this.GetReasons();
          this.CheckWindowOverride('submission');
          this.CheckWindowOverride('transaction');
          const interval = setInterval(() => {
            this.service.GetRemainingTime(this.contributeMarket.marketId, 'Contribution StartOrFinish').then(res => {
              this.timeToOpenOrClose = res.time;
              if (this.currentTimeColumn === '') {
                this.currentTimeColumn = res.timeColumn;
              } else if ((this.currentTimeColumn !== res.timeColumn) || res.time.split('0').join('').replace(/\:/gi, '').trim() === '') {
                if (this.currentTimeColumn === 'Contribution Start' && res.timeColumn === 'Contribution Finish') {
                  this.GetContributions();
                  this.GetTransactions();
                } else if ((this.currentTimeColumn === 'Contribution Finish' && res.timeColumn === 'Contribution Start') || (res.timeColumn === 'Contribution Finish' && this.allowSubmissions === false)) {
                  this.contributeMarket.isMarketOpen = false;
                  this.contributeMarketTemp.isMarketOpen = false;
                }
                this.currentTimeColumn = res.timeColumn;
              }
            });
          }, 1000);
          this.intervals.push(interval);
        } else {
          this.discardChanges = true;
          this.router.navigate(['contribute/contribute'], { queryParams: { ID: this.contributeMarket.marketId } });
        }
      });
    });
  }

  /**
   * Checks whether transaction section should display.
   * @returns Whether to show transactions or not
   */
  public showTransaction(): Boolean {
    return (this.allowTransactions && this.contributeMarket.isAllowedUser === true && ((this.hasCurrentContributions === true && this.service.currentUser.isReadOnly === true) || this.service.currentUser.isReadOnly === false));
  }

  /**
   * Checks whether submission section should display.
   * @returns Whether to show submissions or not 
   */
  public showSubmission(): Boolean {
    return (this.contributeMarket.isMarketOpen === true && this.contributeMarket.isAllowedUser === true && ((this.hasCurrentContributions === true && this.service.currentUser.isReadOnly === true) || this.service.currentUser.isReadOnly === false));
  }

  /**
   * Format the specify number into the users locale.
   * @param value The value to format
   * @returns string containin hte formatted number
   */
  public FormatNumber(value: any) {
    const currentLang = this.service.getLocalLanguage();
    if (!isNaN(Number(value)) && value !== '') {
      value = new Intl.NumberFormat(currentLang).format(value);
    }
    return value;
  }

  /**
   * Sets the short description.
   */
  private SetShortDescription(): void {
    this.translate.get('Contribute.Messages.msgParaShortDes').subscribe((translated: string) => {
      this.http.get<CultureSettings>(this.baseUrl + 'api/contribute/getDecimalPlaces/' + this.contributeMarket.marketId + '/' + this.service.getLocalLanguage()).subscribe(result => {
        this.shortdescription = translated.replace(/\@rate/gi, result.decimalPlaces).replace('@culture', result.cultureName);
      }, error => {
        console.error(error);
      });
    });
  }

  /**
   * Checks the market type.
   * @param marketId The ID of the current market
   * @returns Whether the market is transactional or not
   */
  private CheckMarketType(marketId: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.service.currentUser.userContributeMarkets.find(e => e.marketId === marketId).isTransactional === true) {
        resolve(true);
      } else {
        resolve(false);
      }
    });
  }

  /**
   * Gets the list of reasons.
   */
  private GetReasons(): void {
    this.http.get<ValueIdPair[]>(this.baseUrl + 'api/contribute/getReasons').subscribe(result => {
      this.reasons = result;
    }, error => {
      console.error(error);
    });
  }

  /**
   * Initializes the market details.
   */
  private InitializeMarket(): void {
    this.spreadsheetUpload = {
      market: null,
      uploadedFile: '',
      fileExtension: ''
    };
    this.xmlUpload = {
      market: null,
      uploadedFile: '',
      fileExtension: ''
    };
    this.local = this.service.getLocalLanguage().substr(0, 2);
    this.contributeMarket = {
      marketId: '',
      marketCode: '',
      isMarketOpen: false,
      isAllowedUser: false,
      isTransactional: false,
      isPartial: false,
      instruments: null
    };
  }

  /**
   * Determines what window is set as open, submission or transaction.
   * @param type The type of market
   */
  public CheckWindowOverride(type: string): void {
    this.http.get<boolean>(this.baseUrl + 'api/contribute/checkWindowOpen/' + type + '/' + this.contributeMarket.marketId).subscribe(result => {
      if (type == 'transaction') {
        this.allowTransactions = result;
      } else if (type == 'submission') {
        this.allowSubmissions = result;
      }
    }, error => {
      console.error(error);
    });
  }

  /**
   *  Fires when data bounds to the table.
   * @param value Numeric value
   * @param decPlaces The number of decimal places
   */
  public OnDataBound(value: number, decPlaces: number): any {
    let currentVal = String(value);
    if (!isNaN(Number(currentVal)) && currentVal !== '') {
      let temp: string = currentVal;
      if (temp.includes('.')) {
        temp = temp.replace('-', '');
        const beforeDec = temp.split('.')[0].length.toString();
        currentVal = formatNumber(Number(currentVal), this.localeID, beforeDec + '.' + decPlaces + '-' + decPlaces);
      }
    }
    return currentVal;
  }

  /**
   * Gets the contributions for the market.
   */
  public GetContributions(): void {
    this.isLoading = true;
    this.service.showSpinner();
    this.http.get<ContributeMarket>(`${this.baseUrl}api/contribute/getContributions/${this.contributeMarket.marketId}`).subscribe(result => {
      let initialAssign: boolean = false;
      if (!this.contributeMarket.instruments) {
        initialAssign = true;
      }
      this.contributeMarket = result;
      this.displayColumns = this.displayColumnsAll;
      let hasVolume: boolean = false;
      let hasAssessed: boolean = false;
      let hasCurrentRate: boolean = false;
      this.contributeMarket.instruments.forEach(i => {
        i.newRate = i.rate;
        i.newVolume = i.volume;
        if (i.calculationType === 'wav' && hasVolume === false) {
          hasVolume = true;
        }
        if (i.assessedRate && hasAssessed === false) {
          hasAssessed = true;
        }
        if (i.rate && hasCurrentRate === false) {
          hasCurrentRate = true;
          this.hasCurrentContributions = true;
        }
      });
      this.FormatNumbers(0);
      if (hasVolume === false) {
        const volumeItems: string[] = ['assessedVolume', 'currentVolume', 'newVolume'];
        this.displayColumns = this.displayColumns.filter(f => !volumeItems.includes(f));
      }
      if (hasAssessed === false) {
        const assessedItems: string[] = ['assessedRate', 'assessedVolume'];
        this.displayColumns = this.displayColumns.filter(f => !assessedItems.includes(f));
      }
      if (hasCurrentRate === false) {
        const currentItems: string[] = ['currentRate', 'currentVolume'];
        this.displayColumns = this.displayColumns.filter(f => !currentItems.includes(f));
      }
      this.contributeMarketTemp = JSON.parse(JSON.stringify(this.contributeMarket));
      this.instrumentLength = this.contributeMarket.instruments.length;
      this.dataSourceNew = new MatTableDataSource(this.contributeMarket.instruments);
      if (initialAssign === true) {
        this.EnablebtnSubmit();
      }
    }, error => {
      console.error(error);
    }).add(() => {
      this.service.hideSpinner();
      this.isLoading = false;
    });
  }

  /**
   * Gets transactions.
   */
  public GetTransactions(): void {
    this.isLoading = true;
    this.service.showSpinner();
    this.http.get<Transaction[]>(`${this.baseUrl}api/contribute/getTransactions/${this.contributeMarket.marketId}`).subscribe(result => {
      this.transRecordsLength = result.length;
      this.translist = result;
      this.UpdateTransTable();
      this.FormatNumbers(1);
    }, error => {
      console.error(error);
    }).add(() => {
      this.service.hideSpinner();
      this.isLoading = false;
    });
  }

  /**
   * Formats the numbers according to the browser's culture.
   * @param from The number to format
   */
  private FormatNumbers(from: number): void {
    if (from === 1) {
      this.translist.forEach(i => {
        let temp = i.rate.replace('-', '');
        let beforeDec = temp.split('.')[0].length.toString();
        let afterDec = temp.split('.')[1].length.toString();
        i.rate = formatNumber(Number(i.rate), this.localeID, beforeDec + '.' + afterDec + '-' + afterDec);
      });
    } else {
      this.contributeMarket.instruments.forEach(i => {
        if (i.decimalPlaces && i.newRate) {
          i.fsaCOF = (Number(i.newRate) + Number(i.spread)).toString();
          let temp = i.newRate.replace('-', '');
          let beforeDec = temp.split('.')[0].length.toString();
          i.newRate = formatNumber(Number(i.newRate), this.localeID, beforeDec + '.' + i.decimalPlaces + '-' + i.decimalPlaces);
          if (i.level === 3) {
            i.assessedRate = formatNumber(Number(i.assessedRate), this.localeID, beforeDec + '.' + i.decimalPlaces + '-' + i.decimalPlaces);
          }
          if (from >= 0) {
            i.rate = i.newRate;
          }
          temp = i.fsaCOF.replace('-', '');
          beforeDec = temp.split('.')[0].length.toString();
          i.fsaCOF = formatNumber(Number(i.fsaCOF), this.localeID, beforeDec + '.' + i.decimalPlaces + '-' + i.decimalPlaces);
        }
        let temp = i.defaultSpread.replace('-', '');
        let beforeDec = temp.split('.')[0].length.toString();
        let afterDec = temp.split('.')[1].length.toString();
        i.defaultSpread = formatNumber(Number(i.defaultSpread), this.localeID, beforeDec + '.' + afterDec + '-' + afterDec);
        temp = i.spread.replace('-', '');
        beforeDec = temp.split('.')[0].length.toString();
        afterDec = temp.split('.')[1].length.toString();
        i.spread = formatNumber(Number(i.spread), this.localeID, beforeDec + '.' + afterDec + '-' + afterDec);
        i.newSpread = i.spread;
        if (i.previousRate) {
          temp = i.previousRate.replace('-', '');
          const parts = temp.split('.');
          beforeDec = parts[0].length.toString();
          afterDec = parts[1] ? parts[1].length.toString() : '0';
          i.previousRate = formatNumber(Number(i.previousRate), this.localeID, beforeDec + '.' + afterDec + '-' + afterDec);
        }
      });
    }
  }

  /**
   * Applying filter for dataSources.
   */
  public ApplyFilter(): void {
    this.dataSourceTrans.filter = this.searchKey.trim().toLowerCase();
  }

  /**
   * Clear and close search user and set to the initial userlist by calling applyfilter.
   */
  public OnSearchClear(): void {
    this.searchKey = '';
    this.ApplyFilter();
  }

  /**
   * Check requirements and enables the submit button.
   */
  private EnablebtnSubmit(): void {
    // this.canEnablebtnSubmit = this.CheckInputs();
  }

  /** Check the possibility of submit */
  CanSubmit(): string {
    let status = '';

    this.contributeMarket.instruments.forEach(i => {
      if (i.spread !== i.newSpread && !i.listReasonId && Number(i.newSpread) != Number(i.defaultSpread)) {
        if (status !== '') {
          status += ', ';
        }
        status += i.instrumentDescription;
      }
    });

    return status;
  }

  /** Click event for the populate button */
  OnClickPopulate() {
    if (this.hasUploadedAcceptedSpreadsheetFile === false) {
      if (this.spreadsheetFilename === '') {
        this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderInvFile', 'Contribute.Messages.msgParaEmptyFile');
      } else {
        this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderInvFile', 'Contribute.Messages.msgParaInvSpreadsheetFileType');
      }
    } else {
      const reader = new FileReader();
      reader.readAsDataURL(this.uploadedSpreadsheetFile);
      reader.onload = (event: any) => {
        const tempInstruments: ContributeMarketRecord[] = JSON.parse(JSON.stringify(this.contributeMarket.instruments));
        this.spreadsheetUpload.market = this.contributeMarket;
        this.spreadsheetUpload.uploadedFile = event.target.result;
        this.service.showSpinner();
        this.http.post<ContributionsFileUploadResponse>(this.baseUrl + 'api/contribute/populateRates', this.spreadsheetUpload).subscribe(result => {
          if (result.response.statusCode === 1) {
            this.contributeMarket.instruments = result.instruments;
            this.contributeMarket.instruments.forEach(o => {
              const old = tempInstruments.find(i => i.instrumentId === o.instrumentId);
              o.newRate = o.rate;
              o.newVolume = o.volume;

              o.rate = old.rate;
              o.volume = old.volume;
            });
            this.FormatNumbers(-1);
            this.dataSourceNew = new MatTableDataSource(this.contributeMarket.instruments);
            this.EnablebtnSubmit();
          } else if (result.response.statusCode === 4) {
            this.service.SaveStatus(true, 'Contribute.header', result.response.statusMessage);
          } else {
            this.service.SaveStatus(false, this.translate.instant('Contribute.header'), result.response.statusMessage, true);
          }
        }, error => {
          console.error(error);
        }).add(() => {
          this.service.hideSpinner();
          if (this.spreadsheetInput) {
            this.hasUploadedAcceptedSpreadsheetFile = false;
            this.spreadsheetFilename = '';
            this.spreadsheetInput.nativeElement.value = '';
            this.spreadsheetUpload = {
              market: null,
              uploadedFile: '',
              fileExtension: ''
            };
          }
        });
      };
      reader.onerror = (error) => {
        console.error('Error: ', error);
        this.service.SaveStatus(false, 'Contribute.header', 'Contribute.Messages.msgErrorUpldFile');
      };
    }
  }

  /** Fires when user changes the upload file */
  spreadsheetInputChange(fileInputEvent: any) {
    const acceptFileTypes: string[] = ['xls', 'xlsx'];
    this.spreadsheetFilename = fileInputEvent.target.files[0].name;
    this.hasUploadedAcceptedSpreadsheetFile = false;
    if (this.spreadsheetFilename) {
      const fileSplit = this.spreadsheetFilename.split('.');
      if (acceptFileTypes.includes(fileSplit[fileSplit.length - 1])) {
        this.hasUploadedAcceptedSpreadsheetFile = true;
        this.uploadedSpreadsheetFile = fileInputEvent.target.files[0];
        this.spreadsheetUpload.fileExtension = fileSplit[fileSplit.length - 1];
      }
      if (this.spreadsheetFilename.length > 40) {
        // this.filename = this.filename.substr(0, 30) + '____.' + fileSplit[1];
      }
    }

    if (this.hasUploadedAcceptedSpreadsheetFile === false) {
      this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderInvFile', 'Contribute.Messages.msgParaInvSpreadsheetFileType');
    }
  }


  /** Click event for the psubmit transaction file */
  OnClickTransactionSubmit() {
    if (this.hasUploadedAcceptedXmlFile === false) {
      if (this.xmlFilename === '') {
        this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderInvFile', 'Contribute.Messages.msgParaEmptyFile');
      } else {
        this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderInvFile', 'Contribute.Messages.msgParaInvXmlFileType');
      }
    } else {
      const reader = new FileReader();
      reader.readAsDataURL(this.uploadedXmlFile);
      reader.onload = (event: any) => {
        const tempInstruments: ContributeMarketRecord[] = JSON.parse(JSON.stringify(this.contributeMarket.instruments));
        this.xmlUpload.market = this.contributeMarket;
        this.xmlUpload.uploadedFile = event.target.result;
        this.service.showSpinner();
        this.http.post<GRSSResponse>(this.baseUrl + 'api/contribute/submitTransactionFile', this.xmlUpload).subscribe(result => {
          if (result.statusCode === 4) {
            this.service.SaveStatus(true, 'Contribute.header', result.statusMessage);
          } else {
            this.service.SaveStatus(false, 'Contribute.header', result.statusMessage);
          }
        }, error => {
          console.error(error);
        }).add(() => {
          this.service.hideSpinner();
          if (this.transactionInput) {
            this.uploadedXmlFile = null;
            this.xmlFilename = '';
            this.transactionInput.nativeElement.value = '';
            this.hasUploadedAcceptedXmlFile = false;
            this.xmlUpload = {
              market: null,
              uploadedFile: '',
              fileExtension: ''
            };
          }
        });
      };
      reader.onerror = (error) => {
        console.error('Error: ', error);
        this.service.SaveStatus(false, 'Contribute.header', 'Contribute.Messages.msgErrorUpldFile');
      };
    }
  }


  /** Fires when user changes the upload xml file */
  transactionInputChange(fileInputEvent: any) {
    const acceptFileTypes: string[] = ['xml'];
    this.xmlFilename = fileInputEvent.target.files[0].name;
    this.hasUploadedAcceptedSpreadsheetFile = false;
    if (this.xmlFilename) {
      const fileSplit = this.xmlFilename.split('.');
      if (acceptFileTypes.includes(fileSplit[fileSplit.length - 1])) {
        this.hasUploadedAcceptedXmlFile = true;
        this.uploadedXmlFile = fileInputEvent.target.files[0];
        this.xmlUpload.fileExtension = fileSplit[0];
      }
    }

    if (this.hasUploadedAcceptedXmlFile === false) {
      this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderInvFile', 'Contribute.Messages.msgParaInvXmlFileType');
    }
  }

  /** Edit event for the contribution */
  OnEditRecord(record: ContributeMarketRecord) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.position = { top: '5%' };
    dialogConfig.data = {
      record: record,
      reasons: this.reasons,
      marketId: this.contributeMarket.marketId
    };
    this.dialog.open(ContributeEditComponent, dialogConfig).afterClosed().subscribe((res: ContributeMarketRecordEdit) => {
      if (res !== null) {
        const index = this.contributeMarket.instruments.indexOf(record, 0);
        this.contributeMarket.instruments[index].newRate = res.rate;
        this.contributeMarket.instruments[index].newSpread = res.spread;
        this.contributeMarket.instruments[index].comments = res.comments;
        this.contributeMarket.instruments[index].listReasonId = res.reasonId;
        this.contributeMarket.instruments[index].fsaCOF = res.fsaCOF;
        this.EnablebtnSubmit();
      }
    });
  }

  /** Click event for the submit button */
  OnSubmitClick() {
    const status = this.CanSubmit();
    if (status === '') {
      this.hasSubmitted = true;
      const tempInstruments: ContributeMarketRecord[] = JSON.parse(JSON.stringify(this.contributeMarket.instruments));
      this.contributeMarket.instruments.forEach(o => {
        o.rate = o.newRate;
        o.volume = o.newVolume;
        o.spread = o.newSpread;
      });
      this.http.post<VerifyResponse>(this.baseUrl + 'api/contribute/verifyRates', this.contributeMarket).subscribe(re => {
        if (re.response.statusCode === 1) {
          this.dialog.open(DialogConfirmComponent, {
            disableClose: true,
            data: {
              title: this.translate.instant('Contribute.Messages.msgHeaderCnfrm'),
              message: re.response.statusMessage + this.translate.instant('Contribute.Messages.msgParaCnfrm'),
              innerHTML: true
            },
            position: { top: '5%' }
          }).afterClosed().subscribe(res => {

            // Evaluate the users response.
            if (res === true) {

              this.service.showSpinner();
              this.http.post<GRSSResponse>(this.baseUrl + 'api/contribute/submitRates', this.contributeMarket).subscribe(result => {
                if (result.statusCode === 1) {
                  this.hasSubmitted = false;
                  this.GetContributions();
                  this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderCont', result.statusMessage);
                } else if (result.statusCode === 4) {
                  this.service.SaveStatus(true, 'Contribute.Messages.msgHeaderCont', result.statusMessage);
                } else {
                  this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderCont', result.statusMessage);
                }
              }, error => {
                console.error(error);
              }).add(() => {
                this.service.hideSpinner();
              });
              this.contributeMarket.instruments.forEach(o => {
                o.newRate = o.rate;
                o.newVolume = o.volume;
                o.newSpread = o.spread;
              });
              this.contributeMarketTemp.instruments.forEach(o => {
                o.newRate = o.rate;
                o.newVolume = o.volume;
                o.newSpread = o.spread;
              });
            } else {
              this.contributeMarket.instruments = tempInstruments;
              this.dataSourceNew = new MatTableDataSource(this.contributeMarket.instruments);
            }
          });
        } else if (re.response.statusCode === 4) {
          this.service.SaveStatus(true, 'Contribute.Messages.msgHeaderCont', re.response.statusMessage);
        } else {
          this.contributeMarket.instruments = tempInstruments;
          this.dataSourceNew = new MatTableDataSource(this.contributeMarket.instruments);
          this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderCont', re.response.statusMessage);
        }
      }, error => {
        console.error(error);
      });
    } else {
      this.service.SaveStatus(false, this.translate.instant('Contribute.Messages.msgHeaderCont'), 'Please add a reason or reasons for changing the spread of following tenors. <br>' + status, true);
    }
  }

  OnCreateTrade() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    dialogConfig.position = { top: '5%' };
    dialogConfig.data = { marketId: this.contributeMarket.marketId };
    this.dialog.open(AddTradeComponent, dialogConfig).afterClosed().subscribe((res: Transaction) => {
      if (res !== null) {
        this.translist.unshift(res);
        this.UpdateTransTable();
      }
    });
  }

  OnEditTrade(element: Transaction) {
    if (element.status != 'Cancelled') {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.disableClose = true;
      dialogConfig.autoFocus = true;
      dialogConfig.width = '60%';
      dialogConfig.position = { top: '5%' };
      dialogConfig.data = { marketId: this.contributeMarket.marketId, trade: element };
      this.dialog.open(AddTradeComponent, dialogConfig).afterClosed().subscribe((res: Transaction) => {
        if (res !== null) {
          const index = this.translist.indexOf(element, 0);
          this.translist.splice(index, 1, res);
          this.UpdateTransTable();
        }
      });
    }
  }

  OnDeleteTrade(element: Transaction) {
    if (element.status != 'Cancelled') {
      const dialogRef = this.dialog.open(DialogConfirmComponent, {
        disableClose: true,
        data: {
          title: this.translate.instant('Contribute.Messages.msgHeaderDeleteTrans'),
          message: this.translate.instant('Contribute.Messages.msgContentDeleteTrans')
        },
        position: { top: '5%' }
      });
      dialogRef.afterClosed().subscribe(result => {

        // Evaluate the users response.
        if (result === true) {
          const index = this.translist.indexOf(element, 0);
          this.translist[index].status = 'Cancelled';
          this.translist[index].hasUpdated = true;
          this.translist[index].changedProps = [{ id: 'status', value: '' }];
          this.UpdateTransTable();
        }
      });
    }
  }

  OnTransSaveClick() {
    this.http.post<GRSSResponse>(this.baseUrl + 'api/contribute/submitTransactions/' + this.contributeMarket.marketId, this.translist).subscribe(result => {
      if (result.statusCode == 1) {
        this.GetTransactions();
        this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderSubmitTrans', 'Contribute.Messages.msgContentSubmitTrans');
      } else {
        this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderSubmitTrans', result.statusMessage);
      }
    }, error => {
      console.error(error);
    });
  }

  UpdateTransTable() {

    // Sets the state
    this.translist.forEach((trans) => {
      switch (trans.state) {
        case 'Pending':
          trans.state = 'watch_later';
          trans.stateColor = 'text-warning';
          break;
        case 'Processing':
          trans.state = 'watch_later';
          trans.stateColor = '';
          break;
        case 'Approved':
          trans.state = 'check_circle';
          trans.stateColor = '';
          break;
        case 'Rejected':
          trans.state = 'cancel';
          trans.stateColor = 'text-danger';
          break;
        case 'Edited':
          trans.state = 'drive_file_rename_outline';
          trans.stateColor = 'text-warning';
          break;
      }
    });
    this.dataSourceTrans = new MatTableDataSource(this.translist);
    this.dataSourceTrans.sort = this.sortTrans;

    // Get the new records to the start
    this.dataSourceTrans.sortingDataAccessor = (item, key) => {
      if (key == 'contributed') {
        return item[key] || 'Ω';
      } else {
        return item[key];
      }
    };
    this.dataSourceTrans.paginator = this.pgTrans;
  }
  checkTradeColumn(element: Transaction, column: string, isValue: boolean = false) {
    let response = element.changedProps && (element.changedProps.filter(e => e.id == column).length > 0) ? 'true' : 'false';
    if (isValue) {
      if (response == 'true') {
        response = element.changedProps.filter(e => e.id == column)[0].value;
      } else {
        response = '';
      }
    }
    return response;
  }
}

/** An interface to access market  details */
interface ContributeMarket {
  marketId: string;
  marketCode: string;
  isMarketOpen: boolean;
  isAllowedUser: boolean;
  isTransactional: boolean;
  isPartial: boolean;
  instruments: ContributeMarketRecord[];
}

/** An interface to access a record of a market contribution */
interface ContributeMarketRecord {
  submissionInstrumentId: string;
  instrumentId: string;
  instrumentDescription: string;
  listReasonId: string;
  level: number;
  defaultSpread: string;
  spread: string;
  newSpread: string;
  fsaCOF: string;
  rate?: string;
  volume?: number;
  comments: string;
  assessedRate?: string;
  assessedVolume?: number;
  previousRate?: string;
  previousVolume?: number;
  notes: string;
  status: string;
  calculationType: string;
  newRate: string;
  newVolume: number;
  decimalPlaces: number;
  decimalPlacesStep: string;
  instrumentAccepted: boolean;
}

/** Interface to access the edited details of the contribution record */
interface ContributeMarketRecordEdit {
  spread: string;
  rate: string;
  comments: string;
  fsaCOF: string;
  level: number;
  reasonId: string;
}


/** Interface to access the culture settings response */
interface CultureSettings {
  decimalPlaces: string;
  cultureName: string;
}


/** Interface for access request for file upload */
interface ContributionsFileUpload {
  market: ContributeMarket;
  uploadedFile: string;
  fileExtension: string;
}

/** Interface to access the response from the file upload */
interface ContributionsFileUploadResponse {
  response: GRSSResponse;
  instruments: ContributeMarketRecord[];
}

/** Interface to access the response from the verify rates */
interface VerifyResponse {
  conflicted: boolean;
  response: GRSSResponse;
}

/** Interface to access transaction data */
export interface Transaction {
  state: string;
  stateColor: string;
  id: string;
  currency: ValueIdPair;
  status: string;
  counterParty: string;
  type: string;
  instrumentType: string;
  identifier: string;
  tradeDate: string;
  settlementDate: string;
  maturityDate: string;
  nominalAmount: number;
  rate: string;
  contributed: string;
  hasUpdated: boolean;
  notes: string;
  comments: string;
  changedProps: ValueIdPair[];
}
